// src/pages/POEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  limit,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../services/firebase";
import { createPO } from "../services/inventoryApi"; // création simple (incrémente onOrder au create)

const DEFAULT_SUPPLIER = "";
const CURRENCY = "XOF";

function rid() {
  return (
    (crypto?.randomUUID && crypto.randomUUID()) ||
    Math.random().toString(36).slice(2)
  );
}

export default function POEditor() {
  const nav = useNavigate();
  const { poId } = useParams(); // si présent => mode édition
  const isEditing = Boolean(poId);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [supplier, setSupplier] = useState(DEFAULT_SUPPLIER);
  const [status, setStatus] = useState("open"); // open | partial | closed
  const [items, setItems] = useState([]); // {rowId,itemId,name,barcode,qtyOrdered,qtyReceived,unitCost}

  // état brut du PO (pour locked/status, etc.)
  const [po, setPo] = useState(null);

  // recherche d’articles
  const [term, setTerm] = useState("");
  const [sugs, setSugs] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  // editable : en création toujours true, en édition seulement si pas locked & open
  const editable =
    !isEditing || (po && !po.locked && (po.status || "open") === "open");

  // --- Totaux ---
  const totals = useMemo(() => {
    const qty = items.reduce((s, r) => s + Number(r.qtyOrdered || 0), 0);
    const cost = items.reduce(
      (s, r) =>
        s + Number(r.qtyOrdered || 0) * Number(r.unitCost || 0),
      0
    );
    return { qty, cost };
  }, [items]);

  // -------------------- CHARGEMENT EN MODE ÉDITION --------------------
  useEffect(() => {
    (async () => {
      if (!isEditing) return;
      try {
        setLoading(true);
        const ref = doc(db, "purchaseOrders", poId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setMsg("PO introuvable.");
          return;
        }
        const data = snap.data() || {};
        setPo({ id: snap.id, ...data });
        setSupplier(data.supplier || "");
        setStatus(data.status || "open");
        const src = Array.isArray(data.items) ? data.items : [];
        setItems(
          src.map((l) => ({
            rowId: rid(),
            itemId: l.itemId,
            name: l.name || "Article",
            barcode: l.barcode || "",
            qtyOrdered: Number(l.qtyOrdered || 0),
            qtyReceived: Number(l.qtyReceived || 0),
            unitCost: Number(l.unitCost || 0),
          }))
        );
      } catch (e) {
        console.error(e);
        setMsg("Erreur chargement PO.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEditing, poId]);

  // -------------------- RECHERCHE ARTICLES --------------------
  async function searchItems() {
    const t = term.trim();
    setSugs([]);
    if (!t) return;

    setSearching(true);
    try {
      // 1) par code-barres exact
      const qCb = query(
        collection(db, "itemsEK"),
        where("barcode", "==", t),
        limit(1)
      );
      const snapCb = await getDocs(qCb);
      if (!snapCb.empty) {
        const d = snapCb.docs[0];
        const it = d.data();
        addLine({
          itemId: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          unitCost: Number(it.cost || 0),
          price: Number(it.price || 0),
        });
        setTerm("");
        setSugs([]);
        return;
      }

      // 2) par nom (préfixe sur nameLower)
      const lower = t.toLowerCase();
      const qy = query(
        collection(db, "itemsEK"),
        orderBy("nameLower"),
        startAt(lower),
        endAt(lower + "\uf8ff"),
        limit(10)
      );
      const snap = await getDocs(qy);
      const arr = [];
      snap.forEach((d) => {
        const it = d.data();
        arr.push({
          id: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          cost: Number(it.cost || 0),
          price: Number(it.price || 0),
        });
      });
      setSugs(arr);
    } catch (e) {
      console.error(e);
      setMsg("Erreur recherche articles.");
    } finally {
      setSearching(false);
    }
  }

  function pickSuggestion(s) {
    addLine({
      itemId: s.id,
      name: s.name,
      barcode: s.barcode || "",
      unitCost: Number(s.cost || 0),
      price: Number(s.price || 0),
    });
    setTerm("");
    setSugs([]);
    inputRef.current?.focus();
  }

  function addLine({ itemId, name, barcode, unitCost }) {
    if (!editable) return;
    setItems((prev) => {
      const f = prev.find((x) => x.itemId === itemId);
      if (f) {
        return prev.map((x) =>
          x.itemId === itemId
            ? { ...x, qtyOrdered: Number(x.qtyOrdered || 0) + 1 }
            : x
        );
      }
      return [
        ...prev,
        {
          rowId: rid(),
          itemId,
          name: name || "Article",
          barcode: barcode || "",
          qtyOrdered: 1,
          qtyReceived: 0,
          unitCost: Number(unitCost || 0),
        },
      ];
    });
  }

  function patchRow(rowId, patch) {
    if (!editable) return;
    setItems((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r))
    );
  }

  function removeRow(rowId) {
    if (!editable) return;
    setItems((prev) => prev.filter((r) => r.rowId !== rowId));
  }

  // -------------------- SAUVEGARDE (CREATE / EDIT) --------------------
  async function handleSave() {
    setMsg("");

    if (!editable) {
      setMsg("Ce PO n’est plus modifiable.");
      return;
    }

    // validations
    if (!items.length) {
      setMsg("Ajoute au moins une ligne.");
      return;
    }
    for (const r of items) {
      if (!r.itemId) {
        setMsg("Une ligne n’a pas d’itemId.");
        return;
      }
      if (Number(r.qtyOrdered || 0) < 0) {
        setMsg(`Quantité invalide pour ${r.name}.`);
        return;
      }
      if (Number(r.unitCost || 0) < 0) {
        setMsg(`Coût unitaire invalide pour ${r.name}.`);
        return;
      }
    }

    try {
      setLoading(true);

      if (!isEditing) {
        // --- CRÉATION ---
        const payload = {
          supplier,
          lines: items.map((r) => ({
            itemId: r.itemId,
            name: r.name,
            qty: Number(r.qtyOrdered || 0),
            unitCost: Number(r.unitCost || 0),
          })),
          createdBy: "anonymous",
          amount: items.reduce(
            (s, r) =>
              s +
              Number(r.qtyOrdered || 0) * Number(r.unitCost || 0),
            0
          ),
        };
        const { id } = await createPO(payload);
        setMsg(`✅ PO créé (#${id}).`);
        nav(`/admin/purchase-orders`);
        return;
      }

      // --- ÉDITION ---
      const poRef = doc(db, "purchaseOrders", poId);

      await runTransaction(db, async (tx) => {
        // READS
        const poSnap = await tx.get(poRef);
        if (!poSnap.exists()) throw new Error("PO introuvable");
        const data = poSnap.data() || {};
        const oldItems = Array.isArray(data.items) ? data.items : [];

        // Index par itemId (ancien & nouveau)
        const oldById = new Map();
        oldItems.forEach((l) => {
          if (l?.itemId) oldById.set(l.itemId, l);
        });
        const newById = new Map();
        items.forEach((l) => newById.set(l.itemId, l));

        // Ensemble des itemIds concernés
        const allIds = new Set([...oldById.keys(), ...newById.keys()]);

        // Lire stockAgg de tous les items concernés
        const aggRefs = Array.from(allIds).map((id) =>
          doc(db, "stockAgg", id)
        );
        const aggSnaps = await Promise.all(
          aggRefs.map((r) => tx.get(r))
        );

        // Calcul delta onOrder
        const deltas = [];
        for (let i = 0; i < aggRefs.length; i++) {
          const itemId = aggRefs[i].id;
          const oldLine = oldById.get(itemId) || {};
          const newLine = newById.get(itemId) || {};

          const oldOrdered = Number(oldLine.qtyOrdered || 0);
          const oldReceived = Number(oldLine.qtyReceived || 0);
          const oldRemaining = Math.max(0, oldOrdered - oldReceived);

          const newOrdered = Number(newLine.qtyOrdered || 0);
          const newReceived = Number(oldLine.qtyReceived || 0); // on conserve qtyReceived existant
          const newRemaining = Math.max(0, newOrdered - newReceived);

          const delta = newRemaining - oldRemaining;

          const snap = aggSnaps[i];
          const curAgg = snap.exists() ? snap.data() : { onOrder: 0 };
          const curOnOrder = Number(curAgg.onOrder || 0);
          const nextOnOrder = Math.max(0, curOnOrder + delta);

          deltas.push({
            ref: aggRefs[i],
            next: {
              ...curAgg,
              onOrder: nextOnOrder,
              updatedAt: serverTimestamp(),
            },
          });
        }

        // Nouveau tableau items du PO (préserver qtyReceived)
        const updatedItems = items.map((r) => {
          const prev = oldById.get(r.itemId) || {};
          return {
            itemId: r.itemId,
            name: r.name || prev.name || "Article",
            barcode: r.barcode || prev.barcode || "",
            qtyOrdered: Number(r.qtyOrdered || 0),
            qtyReceived: Number(prev.qtyReceived || 0),
            unitCost: Number(r.unitCost || 0),
          };
        });

        // ECRITURES
        tx.update(poRef, {
          supplier: supplier || null,
          status,
          items: updatedItems,
          updatedAt: serverTimestamp(),
        });

        deltas.forEach((d) => {
          tx.set(d.ref, d.next, { merge: true });
        });
      });

      setMsg("✅ PO mis à jour.");
      nav("/admin/purchase-orders");
    } catch (e) {
      console.error(e);
      setMsg("❌ " + e.message);
    } finally {
      setLoading(false);
    }
  }

  const saveDisabled =
    loading || (isEditing && (po?.locked === true || po?.status !== "open"));

  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">
          {isEditing ? "Éditer le PO" : "Nouveau PO"}
        </h3>
        <div className="d-flex flex-column align-items-end">
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => nav(-1)}
              disabled={loading}
            >
              Retour
            </button>
            <button
              className="btn btn-dark"
              type="button"
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : null}
              Enregistrer
            </button>
          </div>
          {po?.locked && (
            <p className="text-danger small mt-2 mb-0">
              Ce PO a déjà été utilisé dans un shipping. Il ne peut plus
              être modifié.
            </p>
          )}
        </div>
      </div>

      {/* En-tête PO */}
      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-12 col-md-5">
            <label className="form-label">Fournisseur</label>
            <input
              className="form-control"
              placeholder="Nom du fournisseur"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Statut</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={!editable}
            >
              <option value="open">Ouvert</option>
              <option value="partial">Partiel</option>
              <option value="closed">Clos</option>
            </select>
          </div>

          <div className="col-12 col-md-4">
            <label className="form-label">Ajouter un article</label>
            <div className="d-flex gap-2 position-relative">
              <input
                ref={inputRef}
                className="form-control"
                placeholder="Code-barres ou nom (préfixe)…"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchItems()}
                disabled={!editable}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={searchItems}
                disabled={!editable || searching}
              >
                {searching ? "…" : "Chercher"}
              </button>

              {sugs.length > 0 && (
                <div
                  className="list-group position-absolute w-100 mt-5 shadow"
                  style={{ zIndex: 5, maxHeight: 260, overflowY: "auto" }}
                >
                  {sugs.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => pickSuggestion(s)}
                    >
                      <div className="fw-semibold">{s.name}</div>
                      <small className="text-muted">
                        CB: {s.barcode || "—"} · Coût:{" "}
                        {s.cost?.toLocaleString?.() || 0} {CURRENCY} · Prix:{" "}
                        {s.price?.toLocaleString?.() || 0} {CURRENCY}
                      </small>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="form-text">
              Nom: utilise le champ <code>nameLower</code> (préfixe).
            </div>
          </div>
        </div>
      </div>

      {/* Lignes */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th style={{ minWidth: 260 }}>Article</th>
                  <th>Code-barres</th>
                  <th className="text-end" style={{ width: 120 }}>
                    Qté commandée
                  </th>
                  <th className="text-end" style={{ width: 140 }}>
                    Qté reçue
                  </th>
                  <th className="text-end" style={{ width: 160 }}>
                    Coût unitaire ({CURRENCY})
                  </th>
                  <th className="text-end" style={{ width: 160 }}>
                    Total ligne ({CURRENCY})
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      Aucune ligne. Cherche un article ci-dessus pour
                      l’ajouter.
                    </td>
                  </tr>
                ) : (
                  items.map((r) => {
                    const lineTotal =
                      Number(r.qtyOrdered || 0) *
                      Number(r.unitCost || 0);
                    return (
                      <tr key={r.rowId}>
                        <td>
                          <div className="fw-semibold">
                            {r.name || "Article"}
                          </div>
                          <div className="small text-muted">
                            {r.itemId}
                          </div>
                        </td>
                        <td>{r.barcode || "—"}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            className="form-control text-end"
                            value={r.qtyOrdered}
                            onChange={(e) =>
                              patchRow(r.rowId, {
                                qtyOrdered: Number(
                                  e.target.value || 0
                                ),
                              })
                            }
                            disabled={!editable}
                          />
                        </td>
                        <td className="text-end">
                          {Number(
                            r.qtyReceived || 0
                          ).toLocaleString()}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            className="form-control text-end"
                            value={r.unitCost}
                            onChange={(e) =>
                              patchRow(r.rowId, {
                                unitCost: Number(
                                  e.target.value || 0
                                ),
                              })
                            }
                            disabled={!editable}
                          />
                        </td>
                        <td className="text-end fw-semibold">
                          {lineTotal.toLocaleString()} {CURRENCY}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            type="button"
                            onClick={() => removeRow(r.rowId)}
                            disabled={!editable}
                          >
                            Retirer
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} className="text-end">
                    Totaux
                  </td>
                  <td className="text-end fw-bold">
                    {totals.qty.toLocaleString()}
                  </td>
                  <td></td>
                  <td></td>
                  <td className="text-end fw-bold">
                    {totals.cost.toLocaleString()} {CURRENCY}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {msg && (
            <div
              className={`mt-3 alert py-2 ${
                msg.startsWith("✅")
                  ? "alert-success"
                  : msg.startsWith("❌")
                  ? "alert-danger"
                  : "alert-info"
              }`}
            >
              {msg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
